import { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { saleAgentTimeSlots } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots';
import { RequestDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots/dto/RequestDto';
import { ResponseDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots/dto/ResponseDto';

import { ESaleAgentClientName, TReturnType } from '@r1-frontend/service-engineer-visit';
import DateOfServiceEngineerVisitWrapper
    from '@r1-frontend/service-engineer-visit/containers/DateOfServiceEngineerVisitWrapper';

import { ActionAlert } from '@r1-frontend/ui-react/components/Alert';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { speedBonusConnectionAnalytics } from '~/src/entities/speed-bonus/analytics';
import { setTimeSlotData, toggleIsShowConnection } from '~/src/entities/speed-bonus/context/actions';
import { confirmTimeSlot } from '~/src/entities/speed-bonus/context/actions/thunk';
import { useContextDispatch, useContextSelector } from '~/src/entities/speed-bonus/context/hooks';
import { TState } from '~/src/store';
import { openChat } from '~/src/store/actions/chat';
import { selectDomain } from '~/src/store/selectors/domain';
import selectProviderId from '~/src/store/selectors/selectProviderId';

const TimeSlots = (): JSX.Element | null => {
    const dispatch = useDispatch();
    const ctxDispatch = useContextDispatch();

    const { domain, providerId } = useSelector((state: TState) => ({
        domain: selectDomain(state),
        providerId: selectProviderId(state),
    }), shallowEqual);

    const businessLogic = useContextSelector(state => state.businessLogic);

    const [timeSlotsRequestResult, setTimeSlotsRequestResult] = useState({
        isFirstRequestError: false,
        isNextRequestError: false,
        isInitialized: false,
    });

    if (!businessLogic?.agreementId) {
        return null;
    }

    const openChatHandler = () => {
        ctxDispatch(toggleIsShowConnection(false));
        dispatch(openChat());
    };

    const getTimeSlotsHandler = async(_: number, body: RequestDto) => {
        const slots = await doRequest.fullBuy<ResponseDto>(
            saleAgentTimeSlots(providerId, body),
        );

        if (slots.isSuccess) {
            if (!timeSlotsRequestResult.isInitialized) {
                setTimeSlotsRequestResult(prev => ({ ...prev, isInitialized: true }));
            }
        } else if (timeSlotsRequestResult.isInitialized) {
            setTimeSlotsRequestResult(prev => ({ ...prev, isNextRequestError: true }));
        } else {
            speedBonusConnectionAnalytics.timeSlotsUnavailable();
            setTimeSlotsRequestResult(prev => ({ ...prev, isFirstRequestError: true }));
        }

        return slots;
    };

    const confirmSelectHandler = (result: TReturnType | null) => {
        if (!result) {
            return;
        }

        ctxDispatch(setTimeSlotData(result));
        ctxDispatch(confirmTimeSlot());
    };

    const confirmationAnalyticsHandler = () => {
        speedBonusConnectionAnalytics.confirmTimeSlotSelection();
    };

    return (
        <>
            {timeSlotsRequestResult.isFirstRequestError
                ? <ActionAlert type={'error'} button={{ label: 'Написать в чат', onClick: openChatHandler }}>
                    <Paragraph4>
                        При получении информации о&nbsp;доступном времени произошла ошибка.
                        Напишите нам в&nbsp;чат&nbsp;&mdash; поможем назначить время визита инженера или подождите нашего звонка.
                    </Paragraph4>
                </ActionAlert>
                : <>
                    {timeSlotsRequestResult.isNextRequestError && (
                        <ActionAlert type={'error'} button={{ label: 'Написать в чат', onClick: openChatHandler }}>
                            <Paragraph4>
                                При получении информации о&nbsp;доступном времени произошла ошибка.
                                Напишите нам в&nbsp;чат&nbsp;&mdash; поможем подобрать другое время.
                            </Paragraph4>
                        </ActionAlert>
                    )}
                    <DateOfServiceEngineerVisitWrapper
                        domain={domain}
                        contractId={businessLogic.agreementId}
                        clientName={ESaleAgentClientName.FULL_BUY_EQUIPMENT_SITE}
                        productId={EProductsId.internet}
                        getTimeSlotsFunc={getTimeSlotsHandler}
                        onConfirm={confirmSelectHandler}
                        gaCategory={'VAS'}
                        onClickConfirm={confirmationAnalyticsHandler}
                    />
                </>
            }
        </>
    );
};

export default TimeSlots;
